// Watermark and Page numbers (Phase 5, browser tools): their settings and the
// page-number wording. Pure — no pdf-lib — so the page can show a preview.

export type WatermarkOptions = {
  text: string;
  /** Points. */
  size: number;
  /** 0–1. */
  opacity: number;
  color: string;
  /** Degrees, counter-clockwise as seen: 45 runs up to the right. */
  angle: 0 | 45;
  /** One big mark in the middle, or repeated across the page. */
  layout: "center" | "tiled";
};

export const DEFAULT_WATERMARK: WatermarkOptions = {
  text: "CONFIDENTIAL",
  size: 64,
  opacity: 0.25,
  color: "#c8203f",
  angle: 45,
  layout: "center",
};

export type NumberPosition =
  | "bottom-center"
  | "bottom-right"
  | "bottom-left"
  | "top-center"
  | "top-right"
  | "top-left";

export type NumberFormat = "n" | "page-n" | "page-n-of-total" | "n-of-total";

export type PageNumberOptions = {
  position: NumberPosition;
  format: NumberFormat;
  /** Number shown on the first numbered page. */
  start: number;
  /** Leave the first page (e.g. a cover) without a number. */
  skipFirst: boolean;
  /** Points. */
  size: number;
};

export const DEFAULT_PAGE_NUMBERS: PageNumberOptions = {
  position: "bottom-center",
  format: "n",
  start: 1,
  skipFirst: false,
  size: 11,
};

/** Distance from the page edge, in points (half an inch). */
export const STAMP_MARGIN = 36;

/** The label for one page. `n` is the number shown; `total` is the last
 *  number shown, so "of" counts only numbered pages. */
export function pageLabel(format: NumberFormat, n: number, total: number) {
  switch (format) {
    case "n":
      return `${n}`;
    case "page-n":
      return `Page ${n}`;
    case "page-n-of-total":
      return `Page ${n} of ${total}`;
    case "n-of-total":
      return `${n} / ${total}`;
  }
}

/** What each page gets: its label, or null when it is skipped. */
export function pageLabels(
  pageCount: number,
  { format, start, skipFirst }: PageNumberOptions,
): (string | null)[] {
  const numbered = skipFirst ? pageCount - 1 : pageCount;
  const last = start + numbered - 1;
  return Array.from({ length: pageCount }, (_, i) => {
    if (skipFirst && i === 0) return null;
    const n = start + i - (skipFirst ? 1 : 0);
    return pageLabel(format, n, last);
  });
}
